/**
 * Touch gestures for ?touchUi — the wheel itself is the control.
 *
 * With the flag on, the joystick pad goes away and a placed wheel is
 * manipulated directly: tap to select, drag to move it in X/Y, pinch to
 * scale, tap off to deselect. Only the add / delete / configs row
 * survives, because those have no direct-manipulation equivalent.
 *
 * This module is the pure half: pointer events in, intents out. No DOM,
 * no THREE, so the gesture rules can be tested without a scene.
 *
 * Deltas are INCREMENTAL — reported since the previous move rather than
 * since the gesture began — because the consumers are the existing
 * additive controller methods (adjustHorizontal / adjustHeight /
 * adjustScale). That also keeps the recogniser stateless about where a
 * wheel currently is.
 */

import type { ScreenPoint } from '@/ar/wheel-renderer'

/** Movement under this stays a tap. Fingers are not styluses; a few
 *  pixels of travel while pressing is normal, and at 10px a deliberate
 *  tap on a wheel was sometimes read as a tiny drag (2026-08-24). */
export const TAP_SLOP_PX = 20

/** Additive scale per pixel of pinch spread. ~250px of spread is a
 *  full +1.0 on the multiplier, which the controller then clamps to
 *  its 0.25–4 range. */
export const PINCH_SCALE_PER_PX = 0.004

interface Pointer {
  readonly x: number
  readonly y: number
}

export interface GestureState {
  readonly pointers: ReadonlyMap<number, Pointer>
  /** Where the single active pointer went down — the tap candidate. */
  readonly downPoint: ScreenPoint | null
  /** Set once travel passes the slop, or once a second finger lands.
   *  Either way this gesture can no longer end as a tap. */
  readonly disqualifiedAsTap: boolean
  /** A lone finger that has passed the slop and owns the wheel's
   *  position. Deliberately separate from disqualifiedAsTap: a finger
   *  left over from a pinch is disqualified from tapping but must NOT
   *  be dragging, or the wheel jumps by the whole pinch span. */
  readonly dragActive: boolean
  /** Distance between the two pointers at the previous move. */
  readonly pinchDistance: number | null
}

export interface MoveResult {
  readonly state: GestureState
  readonly drag?: { dx: number; dy: number }
  readonly pinch?: { scaleDelta: number }
}

export interface UpResult {
  readonly state: GestureState
  readonly tap?: ScreenPoint
}

export function initialGestureState(): GestureState {
  return {
    pointers: new Map(),
    downPoint: null,
    disqualifiedAsTap: false,
    dragActive: false,
    pinchDistance: null,
  }
}

function distanceBetween(a: Pointer, b: Pointer): number {
  return Math.hypot(a.x - b.x, a.y - b.y)
}

function twoPointers(pointers: ReadonlyMap<number, Pointer>): [Pointer, Pointer] | null {
  if (pointers.size !== 2) return null
  const [a, b] = Array.from(pointers.values())
  return [a, b]
}

export function onPointerDown(
  state: GestureState,
  p: { id: number } & Pointer,
): GestureState {
  const pointers = new Map(state.pointers)
  pointers.set(p.id, { x: p.x, y: p.y })

  const pair = twoPointers(pointers)
  return {
    pointers,
    // Only a lone finger is a tap candidate.
    downPoint: pointers.size === 1 ? { x: p.x, y: p.y } : null,
    // A second finger ends any tap intent — a pinch must not also
    // register as a tap on the empty space between the fingers.
    disqualifiedAsTap: state.disqualifiedAsTap || pointers.size > 1,
    // A new finger never lands mid-drag.
    dragActive: false,
    pinchDistance: pair == null ? null : distanceBetween(pair[0], pair[1]),
  }
}

export function onPointerMove(
  state: GestureState,
  p: { id: number } & Pointer,
): MoveResult {
  const previous = state.pointers.get(p.id)
  // A move for a pointer we never saw go down (it started on some other
  // element, or the down was swallowed) controls nothing.
  if (previous == null) return { state }

  const pointers = new Map(state.pointers)
  pointers.set(p.id, { x: p.x, y: p.y })

  // ── Two fingers: pinch, and explicitly NOT a drag ──────────────────
  // Pinching moves both fingers, so a naive reading sees translation
  // too. Blending them makes the wheel wander while being scaled.
  const pair = twoPointers(pointers)
  if (pair != null) {
    const distance = distanceBetween(pair[0], pair[1])
    const previousDistance = state.pinchDistance
    const next: GestureState = {
      pointers,
      downPoint: null,
      disqualifiedAsTap: true,
      dragActive: false,
      pinchDistance: distance,
    }
    if (previousDistance == null) return { state: next }
    return {
      state: next,
      pinch: { scaleDelta: (distance - previousDistance) * PINCH_SCALE_PER_PX },
    }
  }

  // ── One finger ────────────────────────────────────────────────────
  if (pointers.size !== 1) {
    return { state: { ...state, pointers } }
  }

  // Dragging requires an origin this finger actually pressed at. A
  // survivor of a pinch has none (downPoint was cleared), so it stays
  // inert until it is lifted and pressed again — otherwise the wheel
  // jumps by the whole pinch span the moment one finger leaves.
  const travelled =
    state.downPoint == null
      ? 0
      : Math.hypot(p.x - state.downPoint.x, p.y - state.downPoint.y)
  const isDragging =
    state.dragActive || (state.downPoint != null && travelled > TAP_SLOP_PX)

  const next: GestureState = {
    pointers,
    downPoint: state.downPoint,
    disqualifiedAsTap: state.disqualifiedAsTap || isDragging,
    dragActive: isDragging,
    pinchDistance: null,
  }
  if (!isDragging) return { state: next }

  return {
    state: next,
    drag: { dx: p.x - previous.x, dy: p.y - previous.y },
  }
}

export function onPointerUp(state: GestureState, id: number): UpResult {
  const pointers = new Map(state.pointers)
  pointers.delete(id)

  const wasLone = state.pointers.size === 1
  const tap = wasLone && !state.disqualifiedAsTap ? state.downPoint : null

  // Fingers still down (a pinch losing one): keep the gesture
  // disqualified so the survivor cannot tap or drag on stale origins.
  if (pointers.size > 0) {
    return {
      state: {
        pointers,
        downPoint: null,
        disqualifiedAsTap: true,
        dragActive: false,
        pinchDistance: null,
      },
    }
  }

  const cleared = initialGestureState()
  return tap == null ? { state: cleared } : { state: cleared, tap }
}

/**
 * World metres covered by one screen pixel at a given distance.
 *
 * The vertical FOV maps the viewport height to a world height at the
 * target's distance; square pixels make the horizontal scale the same.
 * Returns 0 for degenerate inputs so callers can treat "no usable
 * scale" as "no movement" rather than propagating NaN into a transform.
 */
export function metersPerPixel({
  distanceM,
  viewportHeightPx,
  verticalFovDeg,
}: {
  distanceM: number
  viewportHeightPx: number
  verticalFovDeg: number
}): number {
  if (!(distanceM > 0) || !(viewportHeightPx > 0) || !(verticalFovDeg > 0)) return 0
  const worldHeight = 2 * distanceM * Math.tan((verticalFovDeg * Math.PI) / 360)
  const scale = worldHeight / viewportHeightPx
  return Number.isFinite(scale) ? scale : 0
}

/**
 * How much of the raw pixel→metre mapping the HORIZONTAL axis keeps.
 *
 * Vertical drag maps 1:1 and reads correctly: screen-up is world-up, and a
 * metre of vertical world motion covers the pixels the FOV says it should.
 * Horizontal does not, because `adjustHorizontal` moves the wheel along its
 * OWN right axis — cross(normal, worldY) — and that axis is foreshortened on
 * screen for any wheel not viewed dead-on. The same metre of world travel
 * then covers fewer pixels, so at 1:1 the wheel outruns the finger. Reported
 * on device 2026-09-04: "a little exaggerated, too sensitive and moving too
 * fast, but at least it is moving in the correct direction".
 *
 * This is a FEEL value, not a derived one — a true correction would divide by
 * the cosine of the angle between the wheel's right axis and the view plane,
 * which is unstable as that angle approaches 90 degrees and would make the
 * gain jump around while the user walks. A flat factor is predictable. Tune
 * it here; it is the only place the horizontal gain lives.
 */
export const HORIZONTAL_DRAG_DAMPING = 0.5

/**
 * Screen drag → world offsets.
 *
 * Both axes are inverted relative to the naive reading, for different
 * reasons, and both were confirmed on device:
 *
 *  - Y: screen Y grows downward while world Y grows up, so dragging
 *    down lowers the wheel.
 *  - X: adjustHorizontal moves along the wheel's own right axis, which
 *    the renderer derives as cross(normal, worldY). For a wheel facing
 *    the viewer that vector points world −X — screen LEFT. So a
 *    positive horizontal offset moves the wheel left, and a rightward
 *    drag has to send a negative one. (2026-08-23 LUAT: "left/right is
 *    moving opposite finger movement".)
 *
 * Both signs are pinned below; they are the most obvious way for direct
 * manipulation to feel wrong.
 */
export function screenDragToOffsets({
  dx,
  dy,
  metersPerPixel: scale,
}: {
  dx: number
  dy: number
  metersPerPixel: number
}): { horizontal: number; height: number } {
  return {
    horizontal: -dx * scale * HORIZONTAL_DRAG_DAMPING,
    height: -dy * scale,
  }
}

// ── Orientation correction ───────────────────────────────────────────

/** A quarter turn, in the same convention as `screen.orientation.angle`
 *  and `deriveViewerRollQuadrant`. */
export type ScreenQuadrant = 0 | 90 | 180 | 270

/**
 * Rotation BETWEEN the DOM frame that pointer events arrive in and the
 * device frame the XR compositor renders in.
 *
 * Pointer coordinates are DOM coordinates. The immersive-ar view is
 * composited in the device's physical orientation. When those two
 * frames disagree by a quarter turn, a drag "up" the rendered image
 * arrives as a horizontal DOM delta — which is the 2026-08-24 report
 * that landscape drags moved the wheel along the wrong axis.
 *
 * Taking the DIFFERENCE rather than the device rotation alone is the
 * whole point. Whether Chrome re-lays-out the DOM during a session is
 * not something this app can assume: the driver's own comment says DOM
 * orientation events are swallowed while a session is active, while the
 * overlay wrapper exists to counteract "Chrome's DOM auto-rotation".
 * Both behaviours have been observed, and which one applies varies by
 * Chrome version and device. The difference is correct in both worlds —
 * if the DOM rotated with the device the terms cancel and nothing is
 * corrected; if it stayed put the full device rotation is applied — so
 * no assumption has to be made or maintained.
 *
 * A runtime that does not report a DOM angle is treated as unrotated,
 * which is what `screen.orientation.angle` means when it is absent.
 */
export function screenToRenderedQuadrant({
  viewerRollQuadrant,
  domRotationAngle,
}: {
  viewerRollQuadrant: ScreenQuadrant
  domRotationAngle: number
}): ScreenQuadrant {
  const domAngle = Number.isFinite(domRotationAngle) ? domRotationAngle : 0
  const snappedDom = ((Math.round(domAngle / 90) * 90) % 360 + 360) % 360
  return (((viewerRollQuadrant - snappedDom) % 360 + 360) % 360) as ScreenQuadrant
}

/**
 * Turns a DOM pointer delta into the rendered frame's delta.
 *
 * Both frames are y-down. The quadrant is how far the rendered frame is
 * turned relative to the DOM one, so the mapping is a plain rotation —
 * lengths are preserved and four turns are the identity, both pinned by
 * test. Quadrant 0 is the identity, which keeps the portrait path that
 * already works bit-for-bit unchanged.
 */
export function rotateScreenDelta({
  dx,
  dy,
  quadrant,
}: {
  dx: number
  dy: number
  quadrant: ScreenQuadrant
}): { dx: number; dy: number } {
  switch (quadrant) {
    case 90:
      return { dx: dy, dy: -dx }
    case 180:
      return { dx: -dx, dy: -dy }
    case 270:
      return { dx: -dy, dy: dx }
    default:
      return { dx, dy }
  }
}

/**
 * Vertical extent of the RENDERED frame, in DOM pixels.
 *
 * `metersPerPixel` divides the world height at the target's distance by
 * this, so it has to be the height of the frame the vertical FOV
 * belongs to. Feeding it the DOM height while the frames are a quarter
 * turn apart makes a landscape drag move the wheel by the wrong
 * distance — a subtler sibling of the wrong-axis bug, and one that
 * survives fixing the axis alone.
 */
export function renderedViewportHeightPx({
  domWidth,
  domHeight,
  quadrant,
}: {
  domWidth: number
  domHeight: number
  quadrant: ScreenQuadrant
}): number {
  return quadrant === 90 || quadrant === 270 ? domWidth : domHeight
}
